import { useMemo, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Closet as ClosetData } from '../lib/queries';
import type { Post } from '../lib/types';
import { isOpened, markOpened } from '../lib/seen';
import { randomHalftoneStyle } from '../lib/halftone';
import './Closet.css';

// How long the doors swing before we hand off to the post route. Kept in
// sync with the --closet-swing duration in Closet.css. Reduced-motion users
// skip the wait entirely (see open()).
const SWING_MS = 620;

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// ---------------------------------------------------------------------------
// Per-closet door styling. Each cabinet has its own door style + knob, mapped
// by drawer SLUG (stable; names can change — see migration 049). Mirrors the
// team's feed mockup (closet-doors-clean.html, DOORS/COMBOS arrays). The
// halftone texture is drawn separately and at random per load.
// ---------------------------------------------------------------------------
type DoorStyle =
  | 'slab' | 'shaker' | 'two' | 'three' | 'raised' | 'bead'
  | 'louver' | 'cath' | 'mull' | 'plank' | 'mission' | 'cope';
type KnobStyle =
  | 'round' | 'plate' | 'square' | 'octagon' | 'semi'
  | 'bar' | 'barlong' | 'cup' | 'finger' | 'ring' | 'hanger';

interface DoorConfig { style: DoorStyle; knob: KnobStyle; }

const DEFAULT_DOOR: DoorConfig = { style: 'shaker', knob: 'hanger' };

// Every door wears a little white hanger as its knob; only the door panel style varies.
const DOOR_CONFIG: Record<string, DoorConfig> = {
  'extended-ba':          { style: 'shaker',  knob: 'hanger' },  // Conversion Closeup
  'out-and-about':        { style: 'two',     knob: 'hanger' },   // Out and About
  'conversion-closeup-1': { style: 'three',   knob: 'hanger' },   // Open Basket 1
  'nancy-conversations':  { style: 'cath',    knob: 'hanger' },   // Nancy Noodles
  'redesign-challenge':   { style: 'mull',    knob: 'hanger' },   // Redesign Challenge
  'conversion-closeup-2': { style: 'mission', knob: 'hanger' },   // Open Basket 2
  // the two solo cabinets at the foot of the wall
  'sales-room':           { style: 'shaker',  knob: 'hanger' },   // Saleroom
  'monthly-zoom-qa':      { style: 'shaker',  knob: 'hanger' },   // Zoom Q+A
};

function doorConfig(slug: string): DoorConfig {
  return DOOR_CONFIG[slug] ?? DEFAULT_DOOR;
}

// Same cover-resolution rules as PostCard so a post looks identical whether
// it's revealed inside a closet or sitting in the Archive grid.
function coverUrl(post: Post): string | null {
  // An explicit per-post thumbnail wins over the media-derived cover.
  if (post.thumbnail_url) return post.thumbnail_url;
  const firstImage = post.image_urls?.[0] ?? null;
  return post.post_type === 'image'
    ? (firstImage ?? post.media?.thumbnail_url ?? null)
    : (post.media?.thumbnail_url ?? null);
}

const HtLayer = () => <span className="ht-layer" aria-hidden="true" />;

// The interior of one door face. The line-drawing pattern comes from the
// .s-<style> class; the halftone dots ride on the .ht-layer overlay(s). Some
// styles are multi-panel (two/three/cope) or have a proud centre (raised).
function DoorInner({ style }: { style: DoorStyle }) {
  if (style === 'two') {
    return (
      <div className="panelbox s-two">
        <span className="panel"><HtLayer /></span>
        <span className="panel"><HtLayer /></span>
      </div>
    );
  }
  if (style === 'three') {
    return (
      <div className="panelbox s-three">
        <span className="panel"><HtLayer /></span>
        <span className="panel"><HtLayer /></span>
        <span className="panel"><HtLayer /></span>
      </div>
    );
  }
  if (style === 'cope') {
    return (
      <div className="panelbox s-cope">
        <span className="panel"><HtLayer /></span>
        <span className="panel"><HtLayer /></span>
      </div>
    );
  }
  if (style === 'raised') {
    return (
      <div className="panelbox s-raised">
        <span className="raised-field"><HtLayer /></span>
      </div>
    );
  }
  return (
    <div className={`panelbox s-${style}`}>
      <HtLayer />
    </div>
  );
}

function Door({ side, cfg }: { side: 'left' | 'right'; cfg: DoorConfig }) {
  return (
    <div className={`closet__door closet__door--${side}`}>
      <DoorInner style={cfg.style} />
      <span className={`knob ${cfg.knob}`} aria-hidden="true" />
    </div>
  );
}

// Merge the random halftone vars with the cavity image var without losing the
// custom-property typing React's CSSProperties doesn't model.
function withCavity(base: CSSProperties, cover: string | null): CSSProperties {
  if (!cover) return base;
  return { ...base, ['--cav' as string]: `url("${cover}")` } as CSSProperties;
}

// ---------------------------------------------------------------------------
// Tall two-door wardrobe. Both doors share the closet's style + knob and carry
// no imagery — the newest post's cover sits in the cavity BEHIND them, revealed
// (in colour) as the doors swing on hover and fully on click. A floating
// Montserrat tag names the closet above the cabinet.
// ---------------------------------------------------------------------------
export function Closet({ drawer, post, cycleActive = false, openDate = null }: ClosetData & { cycleActive?: boolean; openDate?: string | null }) {
  const navigate = useNavigate();
  const [opening, setOpening] = useState(false);
  // Fresh halftone recipe on every mount → "randomized each load".
  const halftone = useMemo(() => randomHalftoneStyle(), []);
  const cfg = doorConfig(drawer.slug);

  const isNew = post ? !isOpened(post.id) : false;
  const empty = !post;
  // In cycle mode: a closet with this-cycle content sits ajar (popped open);
  // one awaiting its drop is shut + "Coming soon".
  const ajar = cycleActive && !empty;
  const comingSoon = cycleActive && empty;
  // A drawer-level cover (drawers.cover_image_url) beats the front post's
  // media — lets e.g. the Saleroom door carry its own standalone thumbnail.
  const cover = drawer.cover_image_url || (post ? coverUrl(post) : null);

  function open() {
    if (!post || opening) return;
    markOpened(post.id);
    if (prefersReducedMotion()) {
      navigate(`/p/${post.slug}`);
      return;
    }
    setOpening(true);
    window.setTimeout(() => navigate(`/p/${post.slug}`), SWING_MS);
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (empty) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      open();
    }
  }

  const cls = [
    'closet',
    isNew && 'closet--new',
    opening && 'closet--opening',
    empty && 'closet--empty',
    ajar && 'closet--ajar',
    comingSoon && 'closet--coming-soon',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={cls}
      style={halftone}
      role={empty ? undefined : 'button'}
      tabIndex={empty ? undefined : 0}
      aria-label={empty
        ? `${drawer.name}${comingSoon ? (openDate ? `, door opens ${openDate}` : ', coming soon this cycle') : ''}`
        : `Open ${drawer.name}: ${post!.title}${isNew ? ' (new)' : ''}`}
      aria-disabled={empty || undefined}
      onClick={empty ? undefined : open}
      onKeyDown={onKeyDown}
    >
      <div className="closet__frame" style={withCavity({}, cover)}>
        <Door side="left" cfg={cfg} />
        <Door side="right" cfg={cfg} />
      </div>
      <div className="closet__labels">
        <span className="closet__tag">{drawer.name}</span>
        {comingSoon && (
          <span className="closet__soon">{openDate ? `Door opens ${openDate}` : 'Coming soon'}</span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The two solo cabinets at the foot of the wall (Saleroom · Zoom Q+A). Each is
// its own single-door box with a floating tag and gap between them — they read
// as two furniture pieces, not one unit. Same reveal + navigation as a tall
// closet; the right cabinet's tag is right-aligned to mirror its hinge.
// ---------------------------------------------------------------------------
function ComboLeaf({ drawer, post, side, cycleActive = false, openDate = null }: ClosetData & { side: 'left' | 'right'; cycleActive?: boolean; openDate?: string | null }) {
  const navigate = useNavigate();
  const [opening, setOpening] = useState(false);
  const halftone = useMemo(() => randomHalftoneStyle(), []);
  const cfg = doorConfig(drawer.slug);

  const isNew = post ? !isOpened(post.id) : false;
  const empty = !post;
  const ajar = cycleActive && !empty;
  const comingSoon = cycleActive && empty;
  // Same drawer-cover override as the tall closets (Saleroom standalone thumb).
  const cover = drawer.cover_image_url || (post ? coverUrl(post) : null);

  function open() {
    if (!post || opening) return;
    markOpened(post.id);
    if (prefersReducedMotion()) {
      navigate(`/p/${post.slug}`);
      return;
    }
    setOpening(true);
    window.setTimeout(() => navigate(`/p/${post.slug}`), SWING_MS);
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (empty) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      open();
    }
  }

  const cls = [
    'closet', 'closet--solo', `closet--${side}`,
    isNew && 'closet--new',
    opening && 'closet--opening',
    empty && 'closet--empty',
    ajar && 'closet--ajar',
    comingSoon && 'closet--coming-soon',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={cls}
      style={halftone}
      role={empty ? undefined : 'button'}
      tabIndex={empty ? undefined : 0}
      aria-label={empty
        ? `${drawer.name}${comingSoon ? (openDate ? `, door opens ${openDate}` : ', coming soon this cycle') : ''}`
        : `Open ${drawer.name}${isNew ? ' (new)' : ''}`}
      aria-disabled={empty || undefined}
      onClick={empty ? undefined : open}
      onKeyDown={onKeyDown}
    >
      <div className="closet__frame" style={withCavity({}, cover)}>
        <Door side={side} cfg={cfg} />
      </div>
      <div className="closet__labels">
        <span className="closet__tag">{drawer.name}</span>
        {comingSoon && (
          <span className="closet__soon">{openDate ? `Door opens ${openDate}` : 'Coming soon'}</span>
        )}
      </div>
    </div>
  );
}

export function ComboCloset({ left, right, cycleActive = false, openDate = null }: { left: ClosetData; right: ClosetData; cycleActive?: boolean; openDate?: string | null }) {
  return (
    <div className="closet-combo">
      <ComboLeaf {...left} side="left" cycleActive={cycleActive} openDate={openDate} />
      <ComboLeaf {...right} side="right" cycleActive={cycleActive} openDate={openDate} />
    </div>
  );
}
